import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useActiveCompany, pickActiveYear } from "../context/ActiveCompanyContext";
import PageHeader from "../components/PageHeader";
import EmptyState from "../components/EmptyState";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import CompanyDashboard from "../components/dashboard/CompanyDashboard";
import GroupDashboard from "../components/dashboard/GroupDashboard";

export default function DashboardPage() {
  const { companyId, setCompanyId, year, setYear } = useActiveCompany();
  const [companies, setCompanies] = useState([]);
  const [createdYears, setCreatedYears] = useState([]);
  const [data, setData] = useState(null);
  const [dataCompanyId, setDataCompanyId] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/companies")
      .then((r) => {
        setCompanies(r.data);
        if (r.data.length > 0 && !r.data.some((c) => c.id === companyId)) setCompanyId(r.data[0].id);
      })
      .catch((e) => toast.error(apiError(e)));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}/periods/years`)
      .then((r) => {
        setCreatedYears(r.data);
        const pick = pickActiveYear(r.data, year);
        if (pick !== year) setYear(pick);
      })
      .catch(() => {});
  }, [companyId, year]); // eslint-disable-line react-hooks/exhaustive-deps

  const company = companies.find((c) => c.id === companyId);
  const isGroup = !!company && (company.is_group_company || companies.some((c) => c.group_parent_id === company.id));

  useEffect(() => {
    if (!company || year == null) { setData(null); setDataCompanyId(null); return; }
    setLoading(true);
    const req = isGroup
      ? api.get("/dashboard/group", { params: { company_id: company.id, year } })
      : api.get("/dashboard/company", { params: { company_id: company.id, year } });
    req.then((r) => { setData(r.data); setDataCompanyId(company.id); })
      .catch((e) => { toast.error(apiError(e)); setData(null); setDataCompanyId(null); })
      .finally(() => setLoading(false));
  }, [companyId, year, companies.length]);

  const years = new Set(createdYears);
  if (year != null) years.add(year);
  (data?.history?.years || (data?.history || []).map((h) => h.year) || []).forEach((y) => years.add(y));
  const yearOptions = [...years].sort();

  return (
    <div data-testid="dashboard-page">
      <PageHeader
        title={isGroup ? "Group Dashboard" : "Dashboard"}
        subtitle={company ? `${company.name} — management cockpit (read-only; charts drill into the canonical modules)` : "Management cockpit"}
        testId="dashboard-header"
        actions={
          <div className="flex items-center gap-2">
            <Select value={companyId} onValueChange={setCompanyId}>
              <SelectTrigger className="w-56" data-testid="dashboard-company-select">
                <SelectValue placeholder="Select company" />
              </SelectTrigger>
              <SelectContent>
                {companies.map((c) => (
                  <SelectItem key={c.id} value={c.id} data-testid={`dashboard-company-option-${c.id}`}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <select
              className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-sm text-slate-700"
              value={year ?? ""}
              onChange={(e) => setYear(Number(e.target.value))}
              data-testid="dashboard-year-picker"
            >
              {yearOptions.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>
        }
      />
      {!company && (
        <EmptyState title="Select a company" description="Choose a company in the header to see its management dashboard. Select Elva Group AB for the consolidated Group dashboard." />
      )}
      {company && loading && <div className="text-sm text-slate-400 py-16 text-center" data-testid="dashboard-loading">Loading dashboard…</div>}
      {company && !loading && data && dataCompanyId === company.id && (isGroup ? <GroupDashboard data={data} /> : <CompanyDashboard data={data} />)}
    </div>
  );
}
